import { ref, computed, watch, nextTick, onUnmounted, getCurrentInstance, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { shouldShowErrorToast } from '../utils/errors';
import { useCustomToast } from '../use/useCustomToast';
import { useInfiniteScrollObserver } from '../use/useInfiniteScrollObserver';
import useUpdateUrl from './useUpdateUrl';
import { parseSearchQuery, type ParsedSearchQuery } from './parseSearchQuery';
import { isSortOrder, toSortOrder, type SortOrderOptional } from '../types/sort.types';
import type {
  SearchFilter,
  SearchParams,
  SearchContext,
  MemberSearchField,
} from '../types/search.types';
import { useDocumentSearchTypeStore } from '../stores/useDocumentSearchTypeStore';
import { SearchSort } from '../types/search.types';
import type { Pagination } from '../types/pagination.types';

export interface ApplyListQueryResetParams {
  search?: string;
  sort?: { sort: string; order: string };
  filter?: SearchFilter;
  quantity?: number;
}

/**
 * Literals, not reads of `@/shared/config`.
 *
 * Reading the shared list at module scope makes every test that mocks `@/shared/config` partially
 * crash on import, and reading it inside a computed makes the comparison silently false. The drift
 * risk is closed by a test that pins both to `QUANTITY_OPTIONS` instead.
 */
const DEFAULT_QUANTITY = 10;
const SHOW_ALL_QUANTITY = 100;

/** The page-size query key, read and written only by screens that opt in. */
const QUANTITY_QUERY_KEY = 'amount';

function normalizeSearchFilterForCompare(filter: SearchFilter): string {
  const normalized = Object.entries(filter).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (value === '' || value === undefined) {
      return acc;
    }
    if (typeof value === 'number' && value === 0) {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
  return JSON.stringify(normalized);
}

export interface SearchConfig<T extends { id: number }> {
  search: string;
  searchType?: 'name' | 'prefix';
  sort: SearchSort;
  filter: SearchFilter;
  context?: string;
  searchUrl: string;
  searchFunction: (params: SearchParams) => Promise<{ data: T[]; metadata: Pagination }>;
  batchSize?: number;
  systemFilters?: string[];
  searchFieldRef?: Ref<MemberSearchField>;
  skipShowAllPrefetch?: boolean;
  persistQuantityInUrl?: boolean;
}

export default function useSearch<T extends { id: number }>(config: SearchConfig<T>) {
  const route = useRoute();
  const toast = useCustomToast();
  const { updateUrl } = useUpdateUrl();
  const { searchType: globalSearchType, setSearchType } = useDocumentSearchTypeStore();

  const loading = ref(true);
  const selectedQuantity = ref<number>(DEFAULT_QUANTITY);
  const currentPage = ref(1);
  const allItems = ref<T[]>([]);
  const hasMoreItems = ref(true);
  const loadingMore = ref(false);
  const batchSize = config?.batchSize || 30;
  const showAllInitialLoadComplete = ref(false);

  const isUpdatingFromUrl = ref(false);
  const isUpdatingFromInternal = ref(false);

  let pendingSearchTerm: string | undefined = undefined;
  let isSearchPending = false;

  const items = ref<T[]>([]);
  const metadata = ref<Pagination | undefined>();

  const hasResults = computed(() => items.value?.length > 0);

  const normalizedConfigSort = config.sort.sort ?? '';
  const normalizedConfigOrder = config.sort.order ?? 'asc';

  const isUsingOrder = computed(
    () => sort.value !== normalizedConfigSort || order.value !== normalizedConfigOrder
  );
  const isUsingFilters = computed(() => {
    const systemKeys = new Set(config.systemFilters ?? []);
    return Object.keys(filter.value).some((key) => !systemKeys.has(key) && !!filter.value[key]);
  });
  const isShowAllMode = computed(() => selectedQuantity.value === SHOW_ALL_QUANTITY);
  const isInfiniteScrollMode = isShowAllMode;

  const searchTerm = ref(config.search);
  const sort = ref(config.sort.sort ?? '');
  const order = ref(config.sort.order ?? 'asc');
  const filter = ref(config.filter);

  const shouldShowTable = computed(
    () =>
      hasResults.value ||
      isUsingFilters.value ||
      isUsingOrder.value ||
      !!searchTerm.value ||
      loading.value
  );

  const shouldShowNoFilterResults = computed(
    () =>
      !hasResults.value &&
      !searchTerm.value &&
      (isUsingFilters.value || isUsingOrder.value) &&
      !loading.value
  );

  const shouldShowNoResults = computed(
    () => !hasResults.value && !!searchTerm.value && !loading.value
  );

  const shouldShowEmptyPage = computed(
    () =>
      !hasResults.value &&
      !searchTerm.value &&
      !isUsingFilters.value &&
      !isUsingOrder.value &&
      !loading.value
  );

  const shouldShowQuantitySelect = computed(() => shouldShowTable.value);

  const notifySearchError = (
    logMessage: string,
    error: unknown,
    toastContent: { title: string; message: string }
  ) => {
    console.error(logMessage, error);
    if (shouldShowErrorToast(error)) {
      toast.showErrorToast({ ...toastContent, type: 'error' });
    }
  };

  const getSearchFieldForUrl = (): { searchField?: MemberSearchField } => {
    if (!config.searchFieldRef) return {};
    return { searchField: config.searchFieldRef.value };
  };

  const getSearchTypeForUrl = (): { searchType?: 'name' | 'prefix' } => {
    if (config.searchFieldRef) return {};
    return { searchType: globalSearchType.value };
  };

  /**
   * `useUpdateUrl` builds the query from scratch and compares the JSON, so a call that omits the
   * key erases it. That is why every write site spreads this. `String`, because the final compare
   * is against the route query, where everything is text.
   */
  const getQuantityForUrl = (): { query?: Record<string, string | undefined> } => {
    if (!config.persistQuantityInUrl) return {};
    return {
      query: {
        [QUANTITY_QUERY_KEY]:
          selectedQuantity.value === DEFAULT_QUANTITY ? undefined : String(selectedQuantity.value),
      },
    };
  };

  /** Without this the page size would become a filter and reach the API as one. */
  const withoutQuantityKey = (urlFilter: SearchFilter): SearchFilter => {
    if (!config.persistQuantityInUrl || !(QUANTITY_QUERY_KEY in urlFilter)) return urlFilter;
    const rest = { ...urlFilter };
    delete rest[QUANTITY_QUERY_KEY];
    return rest;
  };

  const handleSortOptions = async (sortCamp: string, orderFlow: string) => {
    const nextOrder = sortCamp === '' ? normalizedConfigOrder : toSortOrder(orderFlow);
    if (sort.value === sortCamp && order.value === nextOrder) return;

    isUpdatingFromInternal.value = true;
    try {
      sort.value = sortCamp;
      order.value = nextOrder;
      currentPage.value = 1;

      await updateUrl({
        search: searchTerm.value,
        ...getSearchTypeForUrl(),
        page: 1,
        sort: { sort: sortCamp, order: nextOrder as SortOrderOptional },
        filter: filter.value,
        ...getSearchFieldForUrl(),
        ...getQuantityForUrl(),
      });
      await nextTick();

      await resetAndPerformSearch();
    } finally {
      isUpdatingFromInternal.value = false;
    }
  };

  const handleFilter = async (filters: SearchFilter) => {
    const currentFilterString = JSON.stringify(filter.value);
    const newFilterString = JSON.stringify(filters);
    if (currentFilterString === newFilterString) return;

    isUpdatingFromInternal.value = true;
    try {
      filter.value = filters;
      currentPage.value = 1;
      await updateUrl({
        search: searchTerm.value,
        ...getSearchTypeForUrl(),
        page: 1,
        sort: { sort: sort.value, order: order.value as SortOrderOptional },
        filter: filters,
        ...getSearchFieldForUrl(),
        ...getQuantityForUrl(),
      });
      await nextTick();

      await resetAndPerformSearch();
    } finally {
      isUpdatingFromInternal.value = false;
    }
  };

  const handleSearch = async (search: string) => {
    loading.value = true;
    isUpdatingFromInternal.value = true;
    try {
      searchTerm.value = search;
      currentPage.value = 1;
      await updateUrl({
        search: search,
        ...getSearchTypeForUrl(),
        page: 1,
        sort: { sort: sort.value, order: order.value as SortOrderOptional },
        filter: filter.value,
        ...getSearchFieldForUrl(),
        ...getQuantityForUrl(),
      });
      await nextTick();

      await resetAndPerformSearch(search);
    } finally {
      isUpdatingFromInternal.value = false;
    }
  };

  const getRequestBatchSize = () => (isShowAllMode.value ? batchSize : selectedQuantity.value);

  const buildSearchParams = (amount: number, pageNum: number, searchTermValue: string) => {
    const isUsingValue =
      sort.value !== '' || isSortOrder(order.value) || Object.keys(filter.value).length > 0;
    const baseParams = {
      search: searchTermValue,
      ...(config.searchFieldRef
        ? { searchField: config.searchFieldRef.value }
        : { searchType: globalSearchType.value }),
      page: pageNum,
      amount,
      searchUrl: config.searchUrl,
      context: config.context as SearchContext | undefined,
    };
    return {
      ...baseParams,
      ...(isUsingValue
        ? {
            sort: { sort: sort.value, order: order.value as SortOrderOptional },
            filter: filter.value,
          }
        : {
            sort: { sort: config.sort.sort, order: config.sort.order as SortOrderOptional },
            filter: config.filter,
          }),
    };
  };

  const applySearchResult = (
    response: { data: T[]; metadata: Pagination },
    batchSizeToUse: number,
    append: boolean
  ) => {
    items.value = response.data;
    metadata.value = response.metadata;

    if (isShowAllMode.value) {
      if (append) {
        allItems.value = [...allItems.value, ...items.value];
      } else {
        allItems.value = [...items.value];
        currentPage.value = 1;
      }
      const totalForHasMore = metadata.value?.total ?? 0;
      hasMoreItems.value = allItems.value.length < totalForHasMore;
    } else {
      hasMoreItems.value = items.value.length === batchSizeToUse;
    }
  };

  const handleSearchError = (error: unknown, append: boolean) => {
    console.error('Error searching items:', error);
    if (shouldShowErrorToast(error)) {
      toast.showErrorToast({
        title: 'Erro ao buscar itens',
        message: 'Tente novamente mais tarde.',
        type: 'error',
      });
      return;
    }
    if (isShowAllMode.value && !append) {
      allItems.value = [];
    }
  };

  const performSearch = async (term = searchTerm.value, page = 1, append = false) => {
    if (append && loadingMore.value) return;
    if (append && !hasMoreItems.value) return;

    loadingMore.value = append;
    loading.value = !append;

    try {
      const batchSizeToUse = getRequestBatchSize();
      const searchParams = buildSearchParams(batchSizeToUse, page, term);
      const response = await config.searchFunction(searchParams);
      applySearchResult(response, batchSizeToUse, append);

      const fetchFullShowAllDatasetIfNeeded = async () => {
        if (config.skipShowAllPrefetch) return;
        if (!isShowAllMode.value) return;
        if (append) return;
        if (showAllInitialLoadComplete.value) return;

        const totalCount = response.metadata?.total;
        const hasMoreThanFirstBatch =
          typeof totalCount === 'number' && totalCount > batchSizeToUse;
        if (!hasMoreThanFirstBatch) return;

        const secondSearchParams = buildSearchParams(totalCount, page, term);
        const secondResponse = await config.searchFunction(secondSearchParams);
        applySearchResult(secondResponse, totalCount, false);
        showAllInitialLoadComplete.value = true;
      };

      await fetchFullShowAllDatasetIfNeeded();
    } catch (error) {
      handleSearchError(error, append);
    } finally {
      loading.value = false;
      loadingMore.value = false;
    }
  };

  const scrollToPagination = async () => {
    await nextTick();
    const paginationNav = document.querySelector<HTMLElement>(
      'nav[role="navigation"][aria-label*="páginas"]'
    );
    if (paginationNav) {
      paginationNav.scrollIntoView({ behavior: 'smooth', block: 'center' });
      paginationNav.querySelector<HTMLButtonElement>('button[aria-current="page"]')?.focus();
    }
  };

  const onPageChanged = async (page: number) => {
    isUpdatingFromInternal.value = true;
    try {
      currentPage.value = page;
      await updateUrl({
        search: searchTerm.value,
        ...getSearchTypeForUrl(),
        page: page,
        sort: { sort: sort.value, order: order.value as SortOrderOptional },
        filter: filter.value,
        ...getSearchFieldForUrl(),
        ...getQuantityForUrl(),
      });
      await nextTick();

      await performSearch(searchTerm.value, page);
      await scrollToPagination();
    } finally {
      isUpdatingFromInternal.value = false;
    }
  };

  const loadMoreItems = async () => {
    if (loadingMore.value || !hasMoreItems.value) return;
    if (allItems.value.length < batchSize) return;

    currentPage.value += 1;
    await performSearch(searchTerm.value, currentPage.value, true);
  };

  useInfiniteScrollObserver({
    enabled: isShowAllMode,
    loadingMore,
    hasMore: hasMoreItems,
    onLoadMore: loadMoreItems,
  });

  const handleQuantityChange = async (value: string | number) => {
    const numericValue = Number(value);

    selectedQuantity.value = numericValue;

    currentPage.value = 1;
    allItems.value = [];
    showAllInitialLoadComplete.value = false;

    if (!config.persistQuantityInUrl) {
      await performSearch();
      return;
    }

    isUpdatingFromInternal.value = true;
    try {
      await updateUrl({
        search: searchTerm.value,
        ...getSearchTypeForUrl(),
        page: 1,
        sort: { sort: sort.value, order: order.value as SortOrderOptional },
        filter: filter.value,
        ...getSearchFieldForUrl(),
        ...getQuantityForUrl(),
      });
      // Without the `nextTick` the guard drops before the route watcher drains, and the
      // suppression turns into a race.
      await nextTick();

      await performSearch();
    } finally {
      isUpdatingFromInternal.value = false;
    }
  };

  const resetAndPerformSearch = async (searchTermToUse?: string) => {
    if (isUpdatingFromUrl.value) return;

    currentPage.value = 1;
    allItems.value = [];
    hasMoreItems.value = true;
    showAllInitialLoadComplete.value = false;
    await performSearch(searchTermToUse);
  };

  const applyListQueryReset = async (params: ApplyListQueryResetParams) => {
    isUpdatingFromInternal.value = true;

    if (params.search !== undefined) {
      searchTerm.value = params.search;
    }
    if (params.sort) {
      sort.value = params.sort.sort;
      order.value = toSortOrder(params.sort.order);
    }
    if (params.filter !== undefined) {
      filter.value = params.filter;
    }
    if (params.quantity !== undefined) {
      selectedQuantity.value = params.quantity;
      allItems.value = [];
      showAllInitialLoadComplete.value = false;
    }

    currentPage.value = 1;
    hasMoreItems.value = true;

    await updateUrl({
      search: searchTerm.value,
      ...getSearchTypeForUrl(),
      page: 1,
      sort: { sort: sort.value, order: order.value as SortOrderOptional },
      filter: filter.value,
      ...getSearchFieldForUrl(),
      ...getQuantityForUrl(),
    });

    isUpdatingFromInternal.value = false;

    await resetAndPerformSearch(params.search);
  };

  const batchedResetAndPerformSearch = (searchTermToUse?: string) => {
    if (searchTermToUse !== undefined) {
      pendingSearchTerm = searchTermToUse;
    }

    if (isSearchPending) return;

    isSearchPending = true;
    nextTick(async () => {
      try {
        const termToUse = pendingSearchTerm ?? searchTerm.value;
        pendingSearchTerm = undefined;
        isSearchPending = false;
        await resetAndPerformSearch(termToUse);
      } catch (error) {
        isSearchPending = false;
        pendingSearchTerm = undefined;
        notifySearchError('Error in batchedResetAndPerformSearch:', error, {
          title: 'Erro ao buscar itens',
          message: 'Tente novamente mais tarde.',
        });
      }
    });
  };

  const stopWatchers: (() => void)[] = [];

  const stopSearchWatcher = watch(searchTerm, async (newSearchTerm) => {
    if (isUpdatingFromInternal.value || isUpdatingFromUrl.value) return;

    if (newSearchTerm !== undefined && newSearchTerm !== null) {
      try {
        batchedResetAndPerformSearch(newSearchTerm);
      } catch (error) {
        notifySearchError('Error in search watcher:', error, {
          title: 'Erro ao atualizar busca',
          message: 'Tente novamente mais tarde.',
        });
      }
    }
  });
  stopWatchers.push(stopSearchWatcher);

  const stopSortFilterWatcher = watch(
    () => [config.sort.sort, config.sort.order, sort.value, order.value, filter.value],
    async () => {
      if (isUpdatingFromInternal.value || isUpdatingFromUrl.value) return;

      try {
        batchedResetAndPerformSearch();
      } catch (error) {
        notifySearchError('Error in sort/filter watcher:', error, {
          title: 'Erro ao atualizar filtros',
          message: 'Tente novamente mais tarde.',
        });
      }
    }
  );
  stopWatchers.push(stopSortFilterWatcher);

  const applySearchFieldFromQuery = (parsed: ParsedSearchQuery) => {
    if (config.searchFieldRef) {
      if (parsed.searchField && parsed.searchField !== config.searchFieldRef.value) {
        config.searchFieldRef.value = parsed.searchField;
      }
      return;
    }
    if (parsed.searchType && parsed.searchType !== globalSearchType.value) {
      setSearchType(parsed.searchType);
    }
  };

  const applyQueryToSearchState = (parsed: ParsedSearchQuery) => {
    if (parsed.term !== undefined && parsed.term !== searchTerm.value) {
      searchTerm.value = parsed.term;
    }
    if (parsed.sort !== undefined && parsed.sort !== sort.value) {
      sort.value = parsed.sort;
    }
    if (parsed.order !== undefined && parsed.order !== order.value) {
      order.value = parsed.order;
    }
    if (
      config.persistQuantityInUrl &&
      parsed.amount !== undefined &&
      parsed.amount !== selectedQuantity.value
    ) {
      selectedQuantity.value = parsed.amount;
    }
    applySearchFieldFromQuery(parsed);
  };

  const mergeFilterFromQuery = (rawUrlFilter: SearchFilter) => {
    const urlFilter = withoutQuantityKey(rawUrlFilter);
    const mergedFilter = { ...filter.value, ...urlFilter };
    if (
      normalizeSearchFilterForCompare(filter.value) !==
      normalizeSearchFilterForCompare(mergedFilter)
    ) {
      filter.value = mergedFilter;
    }
  };

  const stopRouteQueryWatcher = watch(
    () => route.query,
    async (newQuery) => {
      if (isUpdatingFromInternal.value) return;

      isUpdatingFromUrl.value = true;

      try {
        const parsed = parseSearchQuery(newQuery);

        applyQueryToSearchState(parsed);
        currentPage.value = parsed.page ?? 1;
        // Same reason as in `initSearch`: the two modes do not coexist.
        if (isShowAllMode.value) {
          currentPage.value = 1;
        }
        mergeFilterFromQuery(parsed.filter);

        await nextTick();
      } catch (error) {
        notifySearchError('Error in route.query watcher:', error, {
          title: 'Erro ao processar parâmetros da URL',
          message: 'Tente recarregar a página.',
        });
      } finally {
        isUpdatingFromUrl.value = false;
      }
    },
    { deep: true }
  );
  stopWatchers.push(stopRouteQueryWatcher);

  const initSearch = async () => {
    cleanup();
    const parsed = parseSearchQuery(route.query);

    // Hydrating the term, sort and filters from the URL moves the same refs the watchers below
    // observe, and each of them would schedule a search of its own on top of the one at the end.
    // The guard stays up through a tick so those watchers see it and stand down.
    isUpdatingFromUrl.value = true;
    try {
      applyQueryToSearchState(parsed);
      if (parsed.page !== undefined) {
        currentPage.value = parsed.page;
      }
      // AC18b: the two modes do not coexist. With the page size hydrated from the URL, `?amount=100`
      // and `?page=N` can arrive together, and asking the batch endpoint for page N answers empty
      // with no pagination left on screen to recover from.
      if (isShowAllMode.value) {
        currentPage.value = 1;
      }
      if (Object.keys(parsed.filter).length > 0) {
        mergeFilterFromQuery(parsed.filter);
      }
      await nextTick();
    } finally {
      isUpdatingFromUrl.value = false;
    }

    await performSearch(searchTerm.value, currentPage.value);
  };

  const cleanup = () => {
    pendingSearchTerm = undefined;
    isSearchPending = false;

    items.value = [];
    allItems.value = [];
    showAllInitialLoadComplete.value = false;
  };

  if (getCurrentInstance()) {
    onUnmounted(() => {
      stopWatchers.forEach((stop) => {
        try {
          stop();
        } catch (error) {
          console.warn('Error stopping watcher:', error);
        }
      });
      stopWatchers.length = 0;

      cleanup();
    });
  }

  const searchWithContext = async (term: string) => {
    currentPage.value = 1;
    allItems.value = [];
    hasMoreItems.value = true;
    await performSearch(term);
  };

  const removeItems = async (deletedIds: number[]) => {
    const initialCount = items.value.length;

    const deletedIdsSet = new Set(deletedIds.map((id) => Number(id)));

    items.value = items.value.filter((item) => {
      const itemId = Number(item.id);
      return !deletedIdsSet.has(itemId);
    });

    if (metadata.value && metadata.value.total !== undefined) {
      metadata.value.total = Math.max(0, metadata.value.total - deletedIds.length);
    }

    if (initialCount === deletedIds.length && currentPage.value > 1) {
      isUpdatingFromInternal.value = true;
      currentPage.value -= 1;
      await updateUrl({
        search: searchTerm.value,
        ...getSearchTypeForUrl(),
        page: currentPage.value,
        sort: { sort: sort.value, order: order.value as SortOrderOptional },
        filter: filter.value,
        ...getSearchFieldForUrl(),
        ...getQuantityForUrl(),
      });
      isUpdatingFromInternal.value = false;
    }

    await performSearch(searchTerm.value, currentPage.value);

    return {
      deletedIds,
    };
  };

  return {
    items,
    metadata,
    loading,
    selectedQuantity,
    currentPage,
    allItems,
    hasMoreItems,
    loadingMore,
    isShowAllMode,
    isInfiniteScrollMode,
    isUsingOrder,
    isUsingFilters,
    hasResults,
    shouldShowTable,
    shouldShowNoFilterResults,
    shouldShowNoResults,
    shouldShowEmptyPage,
    shouldShowQuantitySelect,
    sort,
    order,
    filter,
    searchTerm,
    handleSearch,
    handleSortOptions,
    handleFilter,
    onPageChanged,
    handleQuantityChange,
    applyListQueryReset,
    initSearch,
    cleanup,
    searchWithContext,
    removeItems,
  };
}
